import { useState, useEffect } from 'react'
import { HashRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom'
import { LanguageProvider } from './i18n/LanguageContext'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import AuthPage        from './components/AuthPage'
import Dashboard       from './components/Dashboard'
import MemoryGame      from './games/memory/MemoryGame'
import WordMatchGame   from './games/wordmatch/WordMatchGame'
import CutleryGame     from './games/cutlery/CutleryGame'
import WordSearchGame  from './games/wordsearch/WordSearchGame'
import SudokuGame      from './games/sudoku/SudokuGame'
import MathsGame            from './games/maths/MathsGame'
import ShoppingListGame     from './games/shoppinglist/ShoppingListGame'
import MoneyGame            from './games/money/MoneyGame'
import PillboxGame          from './games/pillbox/PillboxGame'
import AttentionGame        from './games/attention/AttentionGame'
import SymmetryGame         from './games/symmetry/SymmetryGame'
import WhatsMissingGame     from './games/whatsmissing/WhatsMissingGame'
import RomanNumbersGame     from './games/roman/RomanNumbersGame'
import CrosswordsGame       from './games/crosswords/CrosswordsGame'
import HoursGame            from './games/hours/HoursGame'
import SynonymsGame         from './games/synonyms/SynonymsGame'
import PuzzleGame           from './games/puzzle/PuzzleGame'
import ChessGame            from './games/chess/ChessGame'
import NumTargetGame        from './games/numtarget/NumTargetGame'
import UserPage           from './components/UserPage'
import SubscriptionPage  from './components/SubscriptionPage'
import StatsPage          from './components/stats/StatsPage'
import VerifyEmailPage   from './components/VerifyEmailPage'
import ResetPasswordPage from './components/ResetPasswordPage'
import AdminHub              from './components/AdminHub'
import { ThemeListView, ThemeDetailView, ThemeObjectView } from './components/AdminPage'
import { UsersListPage, UserDetailPage }  from './components/AdminUsersPage'
import AdminPrintablesPage    from './components/AdminPrintablesPage'
import AdminActivitiesPage   from './components/AdminActivitiesPage'
import AdminActivityDetailPage from './components/AdminActivityDetailPage'
import AdminStatsPage        from './components/AdminStatsPage'
import ClassicThemeEditor    from './components/ClassicThemeEditor'
import AttentionWordEditor   from './components/AttentionWordEditor'
import SynonymThemeEditor    from './components/SynonymThemeEditor'
import DownloadsPage          from './components/DownloadsPage'
import TermsPage             from './components/TermsPage'
import ProfessionalHub        from './professional/ProfessionalHub'
import ProfessionalPlaybooks  from './professional/ProfessionalPlaybooks'
import PlaybookEditor         from './professional/PlaybookEditor'
import PatientSession         from './professional/PatientSession'
import ProfessionalStats      from './professional/ProfessionalStats'
import { professional as proApi, type PlaybookData, type ProfessionalProfile, type PlaybookSessionData, type PlaybookItemData } from './api/client'

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => { window.scrollTo(0, 0) }, [pathname])
  return null
}

function AppRoutes() {
  const { user, isLoading, loginWithResponse } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  if (isLoading) return <LoadingScreen />

  if (!user) {
    if (location.pathname === '/verify-email') {
      return (
        <VerifyEmailPage
          onSuccess={(token, userData) => { loginWithResponse(token, userData) }}
          onBack={() => navigate('/')}
        />
      )
    }
    if (location.pathname === '/reset-password') {
      return <ResetPasswordPage onBack={() => navigate('/')} />
    }
    if (location.pathname === '/terms') {
      return <TermsPage onBack={() => navigate('/')} />
    }
    if (location.pathname === '/signup') {
      return <AuthPage initialTab="signup" />
    }
    if (location.pathname === '/login') {
      return <AuthPage initialTab="login" />
    }
    return <AuthPage />
  }

  const from = (location.state as { from?: string } | null)?.from ?? '/'

  return (
    <Routes>
      <Route path="/" element={
        <Dashboard
          onSelectGame={(game) => navigate(`/${game}`)}
          onGoToSubscription={() => navigate('/subscription', { state: { from: '/' } })}
          onGoToDownloads={() => navigate('/downloads')}
          onGoToProfessional={() => navigate('/professional')}
          onGoToStats={() => navigate('/stats')}
        />
      } />
      <Route path="/memory"     element={<MemoryGame     onBack={() => navigate('/')} onGoToSubscription={() => navigate('/subscription', { state: { from: '/memory' } })} />} />
      <Route path="/wordmatch"  element={<WordMatchGame  onBack={() => navigate('/')} onGoToSubscription={() => navigate('/subscription', { state: { from: '/wordmatch' } })} />} />
      <Route path="/cutlery"    element={<CutleryGame    onBack={() => navigate('/')} />} />
      <Route path="/wordsearch" element={<WordSearchGame onBack={() => navigate('/')} onGoToSubscription={() => navigate('/subscription', { state: { from: '/wordsearch' } })} />} />
      <Route path="/sudoku"     element={<SudokuGame     onBack={() => navigate('/')} />} />
      <Route path="/maths"         element={<MathsGame         onBack={() => navigate('/')} />} />
      <Route path="/shoppinglist"  element={<ShoppingListGame  onBack={() => navigate('/')} />} />
      <Route path="/eurmoney"      element={<MoneyGame         onBack={() => navigate('/')} />} />
      <Route path="/pillbox"       element={<PillboxGame       onBack={() => navigate('/')} />} />
      <Route path="/attention"     element={<AttentionGame     onBack={() => navigate('/')} onGoToSubscription={() => navigate('/subscription', { state: { from: '/attention' } })} />} />
      <Route path="/symmetry"      element={<SymmetryGame      onBack={() => navigate('/')} />} />
      <Route path="/whats_missing" element={<WhatsMissingGame  onBack={() => navigate('/')} />} />
      <Route path="/roman"         element={<RomanNumbersGame  onBack={() => navigate('/')} />} />
      <Route path="/crosswords"    element={<CrosswordsGame    onBack={() => navigate('/')} />} />
      <Route path="/hours"         element={<HoursGame         onBack={() => navigate('/')} onGoToSubscription={() => navigate('/subscription', { state: { from: '/hours' } })} />} />
      <Route path="/synonyms"      element={<SynonymsGame      onBack={() => navigate('/')} />} />
      <Route path="/puzzle"        element={<PuzzleGame        onBack={() => navigate('/')} />} />
      <Route path="/chess"         element={<ChessGame         onBack={() => navigate('/')} />} />
      <Route path="/numtarget"     element={<NumTargetGame     onBack={() => navigate('/')} />} />
      <Route path="/profile"    element={
        <UserPage
          onBack={() => navigate('/')}
          onGoToSubscription={() => navigate('/subscription', { state: { from: '/profile' } })}
          onGoToStats={() => navigate('/stats')}
        />
      } />
      <Route path="/stats" element={
        <StatsPage onBack={() => navigate('/profile')} />
      } />
      <Route path="/subscription" element={
        <SubscriptionPage onBack={() => navigate(from)} />
      } />
      {/* Admin routes — all guard-checked */}
      <Route path="/admin" element={
        user.role === 'ADMIN'
          ? <AdminHub onBack={() => navigate('/')} />
          : <Navigate to="/" replace />
      } />
      <Route path="/admin/themes" element={
        user.role === 'ADMIN'
          ? <ThemeListView
              onSelect={(slug) => navigate(`/admin/themes/${slug}`)}
              onBack={() => navigate('/admin')}
            />
          : <Navigate to="/" replace />
      } />
      <Route path="/admin/themes/:slug" element={
        user.role === 'ADMIN'
          ? <ThemeDetailWrapper onBack={() => navigate('/admin/themes')} />
          : <Navigate to="/" replace />
      } />
      <Route path="/admin/themes/:slug/objects/:imageId" element={
        user.role === 'ADMIN'
          ? <ThemeObjectViewWrapper />
          : <Navigate to="/" replace />
      } />
      <Route path="/admin/users" element={
        user.role === 'ADMIN'
          ? <UsersListPage
              onSelect={(id) => navigate(`/admin/users/${id}`)}
              onBack={() => navigate('/admin')}
            />
          : <Navigate to="/" replace />
      } />
      <Route path="/admin/users/:id" element={
        user.role === 'ADMIN'
          ? <UserDetailWrapper onBack={() => navigate('/admin/users')} />
          : <Navigate to="/" replace />
      } />
      <Route path="/admin/printables" element={
        user.role === 'ADMIN'
          ? <AdminPrintablesPage onBack={() => navigate('/admin')} />
          : <Navigate to="/" replace />
      } />
      <Route path="/admin/activities" element={
        user.role === 'ADMIN'
          ? <AdminActivitiesPage onBack={() => navigate('/admin')} />
          : <Navigate to="/" replace />
      } />
      <Route path="/admin/activities/:slug" element={
        user.role === 'ADMIN'
          ? <AdminActivityDetailWrapper onBack={() => navigate('/admin/activities')} />
          : <Navigate to="/" replace />
      } />
      <Route path="/admin/classic" element={
        user.role === 'ADMIN'
          ? <ClassicThemeEditor onBack={() => navigate('/admin/activities')} />
          : <Navigate to="/" replace />
      } />
      <Route path="/admin/words" element={
        user.role === 'ADMIN'
          ? <AttentionWordEditor onBack={() => navigate('/admin/activities')} />
          : <Navigate to="/" replace />
      } />
      <Route path="/admin/synonyms" element={
        user.role === 'ADMIN'
          ? <SynonymThemeEditor onBack={() => navigate('/admin/activities')} />
          : <Navigate to="/" replace />
      } />
      <Route path="/admin/stats" element={
        user.role === 'ADMIN'
          ? <AdminStatsPage onBack={() => navigate('/admin')} />
          : <Navigate to="/" replace />
      } />
      <Route path="/downloads" element={
        <DownloadsPage
          onBack={() => navigate('/')}
          onGoToSubscription={() => navigate('/subscription', { state: { from: '/downloads' } })}
        />
      } />
      {/* Professional routes */}
      <Route path="/professional" element={
        <ProfessionalHub
          onBack={() => navigate('/')}
          onGoToSubscription={() => navigate('/subscription', { state: { from: '/professional' } })}
        />
      } />
      <Route path="/professional/profiles/:profileId/playbooks" element={<ProfessionalPlaybooks />} />
      <Route path="/professional/profiles/:profileId/playbooks/:playbookId/edit" element={<ProfessionalEditorWrapper />} />
      <Route path="/professional/profiles/:profileId/playbooks/:playbookId/session" element={<ProfessionalSessionWrapper />} />
      <Route path="/professional/profiles/:profileId/stats" element={<ProfessionalProfileStatsWrapper />} />
      <Route path="/terms" element={<TermsPage onBack={() => navigate(-1)} />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

// ── Professional wrapper components ───────────────────────────

function ProfessionalEditorWrapper() {
  const navigate  = useNavigate()
  const location  = useLocation()
  const { profileId } = useParams<{ profileId: string; playbookId: string }>()
  const state = location.state as { playbook?: PlaybookData; profile?: ProfessionalProfile } | null
  const playbook = state?.playbook
  const profile  = state?.profile

  if (!playbook || !profileId) return <Navigate to="/professional" replace />

  const backToPlaybooks = () =>
    navigate(`/professional/profiles/${profileId}/playbooks`, { state: { profile } })

  return (
    <PlaybookEditor
      playbook={playbook}
      onBack={backToPlaybooks}
      onSaved={backToPlaybooks}
    />
  )
}

function ProfessionalSessionWrapper() {
  const navigate  = useNavigate()
  const location  = useLocation()
  const { profileId } = useParams<{ profileId: string }>()
  const state = location.state as {
    session?: PlaybookSessionData
    items?:   PlaybookItemData[]
    profile?: ProfessionalProfile
  } | null

  const session = state?.session
  const items   = state?.items
  const profile = state?.profile

  if (!session || !items) return <Navigate to="/professional" replace />

  return (
    <PatientSession
      session={session}
      playbookItems={items}
      onUnlocked={() =>
        navigate(`/professional/profiles/${profileId}/playbooks`, { state: { profile } })
      }
    />
  )
}

function ProfessionalProfileStatsWrapper() {
  const navigate  = useNavigate()
  const location  = useLocation()
  const { profileId } = useParams<{ profileId: string }>()
  const stateProfile = (location.state as { profile?: ProfessionalProfile } | null)?.profile
  const [profile, setProfile] = useState<ProfessionalProfile | null>(stateProfile ?? null)

  useEffect(() => {
    if (!stateProfile && profileId) {
      proApi.listProfiles()
        .then(all => {
          const p = all.find(pr => pr.id === profileId)
          if (p) setProfile(p)
        })
        .catch(() => {})
    }
  }, [profileId]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!profileId) return <Navigate to="/professional" replace />

  return (
    <ProfessionalStats
      profiles={profile ? [profile] : []}
      onBack={() => navigate('/professional')}
    />
  )
}

// ── Admin wrappers ────────────────────────────────────────────

function LoadingScreen() {
  return (
    <div style={loadStyles.root}>
      <span style={loadStyles.leaf}>🌿</span>
    </div>
  )
}

function ThemeDetailWrapper({ onBack }: { onBack: () => void }) {
  const { slug } = useParams<{ slug: string }>()
  if (!slug) return null
  return <ThemeDetailView slug={slug} onBack={onBack} />
}

function ThemeObjectViewWrapper() {
  const navigate = useNavigate()
  const { slug, imageId } = useParams<{ slug: string; imageId: string }>()
  if (!slug || !imageId) return null
  return <ThemeObjectView themeSlug={slug} imageId={parseInt(imageId, 10)} onBack={() => navigate(`/admin/themes/${slug}`)} />
}

function UserDetailWrapper({ onBack }: { onBack: () => void }) {
  const { id } = useParams<{ id: string }>()
  if (!id) return null
  return <UserDetailPage userId={id} onBack={onBack} />
}


function AdminActivityDetailWrapper({ onBack }: { onBack: () => void }) {
  const { slug } = useParams<{ slug: string }>()
  if (!slug) return null
  return <AdminActivityDetailPage slug={slug} onBack={onBack} />
}

export default function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <HashRouter>
          <ScrollToTop />
          <AppRoutes />
        </HashRouter>
      </AuthProvider>
    </LanguageProvider>
  )
}

const loadStyles: Record<string, React.CSSProperties> = {
  root: {
    minHeight:      '100vh',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    background:     '#FAF6EF',
  },
  leaf: {
    fontSize:  '3rem',
    animation: 'floatLeaf 2s ease-in-out infinite',
  },
}
